import { Box, ClickAwayListener, useTheme } from '@mui/material'
import { CloseIcon } from 'assets/icons'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import { useCalendar } from 'components/providers/CalendarProvider'
import { isSameDay, isToday } from 'date-fns'
import dayjs, { Dayjs } from 'dayjs'
import { IEventDetails } from 'hooks/useEventDetails'
import { getDefaultTimezone } from 'index'
import { useEffect, useRef, useState } from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { DocType } from 'types/api/changes'
import { LocalStorageKey } from 'types/services/localStorage'
import { is24meApp } from 'utils/appType'

import DatePickPopup from '../popups/eventInfo/DatePickPopup'
import { buildTimeOptions, TimePickerBox } from './TimePickerBox'

export enum PickerType {
  START,
  END
}

export interface DatePickerProps {
  pickerType: PickerType
  allDay: boolean
  setStartTime: (date: number) => void
  startTime: number
  setEndTime: (date: number) => void
  endTime: number
  timeZone: string | null | undefined
  setTimeZone: (tz?: string | undefined | null) => void
  duration: number
  eventHook: IEventDetails
  shouldShowTimeDropdown?: boolean
  dateFormat?: (timestamp: number) => string
  timeFormat?: (timestamp: number) => string
  hideDate?: boolean
  hideTime?: boolean
  readOnly?: boolean
  expanded?: boolean
}

const utc = require('dayjs/plugin/utc')
const timezone = require('dayjs/plugin/timezone') // dependent on utc plugin
dayjs.extend(utc)
dayjs.extend(timezone)

export function EventDatePicker(props: DatePickerProps) {
  const theme = useTheme()
  const pickerType = props.pickerType
  const {
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    allDay,
    timeZone,
    setTimeZone,
    shouldShowTimeDropdown,
    duration,
    eventHook,
    readOnly
  } = props

  Logger.blue('EventDatePicker render', {
    startTime,
    endTime,
    allDay,
    timeZone
  })

  const tz =
    eventHook.timeZone && eventHook.timeZone?.length > 0
      ? eventHook.timeZone
      : getDefaultTimezone()

  const sharedUnderlineColor =
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.34)'
      : 'rgba(0, 0, 0, 0.32)'

  let zonedStart = dayjs(startTime).tz(tz)
  let zonedEnd = dayjs(endTime).tz(tz)

  if (allDay) {
    // All-day events are stored at UTC midnight; render them in UTC so the
    // displayed calendar date matches the stored instant in every TZ.
    zonedStart = dayjs(startTime).tz('UTC')
    zonedEnd = dayjs(endTime).tz('UTC')
  }

  const [dateState, setDateState] = useState(false)

  const openDate = () => {
    setDateState(true)
  }

  const calendar = useCalendar()

  let dayjsTime = pickerType === PickerType.START ? zonedStart : zonedEnd

  if (!dayjsTime.isValid()) {
    dayjsTime = dayjs()
  }

  const [time, setTime] = useState<Dayjs | null>(dayjsTime)

  useEffect(() => {
    if (props.eventHook.getValues('someday') === '0') {
      setTime(pickerType === PickerType.START ? zonedStart : zonedEnd)
    }
  }, [startTime, endTime])

  const onAcceptDate = (date: Dayjs | null, time?: boolean) => {
    setDateState(false)

    if (date && !date.isValid()) {
      Logger.orange('EventDatePicker', 'Ignored invalid date input')
      return
    }

    let newDate = date?.tz(props.eventHook.timeZone) ?? dayjs()

    if (!newDate.isValid()) {
      Logger.orange('EventDatePicker', 'Ignored invalid normalized date')
      return
    }

    const baseDate = (pickerType === PickerType.START
      ? zonedStart
      : zonedEnd
    ).isValid()
      ? pickerType === PickerType.START
        ? zonedStart
        : zonedEnd
      : dayjs()

    const targetTz = props.eventHook.timeZone ?? tz
    const mergedIso = time
      ? `${baseDate.format('YYYY-MM-DD')}T${newDate.format('HH:mm:ss.SSS')}`
      : `${newDate.format('YYYY-MM-DD')}T${baseDate.format('HH:mm:ss.SSS')}`

    newDate = dayjs.tz(mergedIso, targetTz)

    if (allDay) {
      newDate = date ?? dayjs()
    }

    if (!newDate.isValid()) {
      Logger.orange('EventDatePicker', 'Ignored invalid merged date')
      return
    }

    setTime(newDate)

    if (pickerType === PickerType.START) {
      const dayVisible =
        newDate.isAfter(
          dayjs(LocalStorage.get(LocalStorageKey.VISIBLE_FROM)).startOf('day')
        ) &&
        newDate.isBefore(
          dayjs(LocalStorage.get(LocalStorageKey.VISIBLE_TO))
            .subtract(1, 'day')
            .endOf('day')
        )
      if (props.eventHook.isNewEvent) {
        if (
          newDate.isAfter(dayjs(calendar.toISO)) ||
          newDate.isBefore(dayjs(calendar.fromISO))
        ) {
          calendar.onGoToDate(newDate.format('YYYY-MM-DD'))
        }
        calendar.onScrollToTime(newDate)
      }

      setStartTime(newDate.valueOf())
      setTimeout(
        () => {
          if (allDay || !props.eventHook.isNewEvent) return
          try {
            ;(calendar.ref as any)?.current.getApi().scrollToTime(
              dayjs(newDate)
                .tz(timeZone ?? getDefaultTimezone())
                .format('HH:mm:ss')
            )
          } catch (e) {
            //can't go to date
          }
        },
        !dayVisible ? 250 : 1
      )
    } else if (pickerType === PickerType.END) {
      console.log(newDate)
      setEndTime(newDate.valueOf())
    }
  }

  const { t } = useTranslation()

  const [showTimeMinutesPopup, setShowTimeMinutesPopup] = useState(false)

  function buildMinutes() {
    return buildTimeOptions(
      pickerType === PickerType.START
        ? zonedStart.startOf('day').subtract(30, 'minute')
        : zonedEnd,
      undefined,
      false,
      true,
      pickerType === PickerType.END,
      pickerType === PickerType.END ? zonedStart : undefined,
      tz
    )
  }

  // Inside your component:
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const longDateFormat =
    (props.dateFormat
      ? props.dateFormat(dayjsTime.valueOf())
      : dayjsTime.format('ll HH:mm')
    ).length > 11

  return (
    <Box
      display={'flex'}
      flexDirection={'row'}
      id={'event-date-picker-box'}
      gap={2}
    >
      {!props.hideDate && (
        <DatePickPopup
          onAccept={onAcceptDate}
          value={startTime === 0 ? dayjs() : dayjsTime}
          open={dateState}
          setOpen={setDateState}
          eventHook={props.eventHook}
          instanceStart={
            pickerType === PickerType.START ? props.startTime : props.endTime
          }
        >
          <div
            id={
              pickerType === PickerType.START
                ? 'start-date-picker'
                : 'end-date-picker'
            }
            style={{
              pointerEvents: readOnly ? 'none' : 'auto',
              cursor: 'pointer',
              width: 'auto',
              flexWrap: 'nowrap',
              height: '100%',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center'
            }}
            onClick={openDate}
          >
            {props.eventHook.getValues('someday') === '1' ? (
              <PopupSectionRow
                actionBg
                onlyTitle
                clickableTitle
                engulfEntireRow
                gap="0rem"
                title={t('clickToSet')}
              />
            ) : (
              <PopupSectionRow
                actionBg
                onlyTitle
                clickableTitle
                actionId="date-picker-box"
                gap="0rem"
                action={() => {
                  //
                }}
                title={
                  <Box
                    onClick={() => {}}
                    sx={{
                      width: 'fit-content',
                      borderBottom: readOnly
                        ? undefined
                        : props.dateFormat
                          ? `1px solid ${sharedUnderlineColor}`
                          : 'none'
                    }}
                  >
                    {props.dateFormat
                      ? props.dateFormat(dayjsTime.valueOf())
                      : `${dayjsTime.format('ll')}`}
                  </Box>
                }
              />
            )}
            {is24meApp() &&
              (props.eventHook.getValues('someday') ?? '0') === '0' && (
                <Controller
                  name="Type"
                  control={props.eventHook.control}
                  defaultValue={props.eventHook.type}
                  render={({ field }) => {
                    if (field.value !== DocType.TASK) return <></>

                    return (
                      <CloseIcon
                        onClick={(e) => {
                          e.stopPropagation()
                          props.eventHook.setSomeday('1')
                        }}
                      />
                    )
                  }}
                />
              )}
          </div>
        </DatePickPopup>
      )}
      {props.eventHook.getValues('someday') !== '1' &&
        !allDay &&
        !props.hideTime && (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex'
            }}
          >
            <Controller
              name="someday"
              control={eventHook.control}
              defaultValue={eventHook.someday}
              render={({ field }) => {
                if (field.value === '1') return <></>

                return (
                  <TimePickerBox
                    actionId={`${pickerType === PickerType.START ? 'start-time-picker-box' : 'end-time-picker-box'}`}
                    readonly={props.readOnly}
                    allDay={allDay}
                    dayjsTime={dayjsTime}
                    timeFormat={props.timeFormat}
                    shouldShowTimeDropdown={shouldShowTimeDropdown}
                    showTimeMinutesPopup={showTimeMinutesPopup}
                    setShowTimeMinutesPopup={setShowTimeMinutesPopup}
                    buildMinutes={buildMinutes}
                    onAcceptDate={onAcceptDate}
                    setTime={setTime}
                    timeoutRef={timeoutRef}
                    withUnderline={!props.readOnly}
                    tz={tz}
                    isStartPicker={pickerType === PickerType.START}
                    shouldScrollToTime={pickerType === PickerType.START}
                    expanded={props.expanded}
                  />
                )
              }}
            />
          </Box>
        )}
    </Box>
  )
}
